import { useFreshSetting } from "@/shared/hooks/useFreshSetting";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpsertItemRollParams } from "../models/RollsReceived";
import {
	getItemRolls,
	getPurchaseOrderDetails,
	getPurchaseOrders,
	removeItemRoll,
	upsertItemRoll,
} from "../repositories/RollsReceivedRepository";

// True when the entity is in RollShop Import mode (screen read-only).
export const useIsRollsReceivedImportMode = (): boolean =>
	useFreshSetting("Rollshop.ImportRolls") === "1";

export const usePurchaseOrders = (
	search: string | null,
	onlyActives: boolean,
) =>
	useQuery({
		queryKey: ["rollsReceived", "pos", search, onlyActives],
		queryFn: ({ signal }) => getPurchaseOrders({ search, onlyActives, signal }),
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

export const usePurchaseOrderDetails = (purchaseOrderId: number | null) =>
	useQuery({
		queryKey: ["rollsReceived", "items", purchaseOrderId],
		queryFn: ({ signal }) =>
			getPurchaseOrderDetails({
				purchaseOrderId: purchaseOrderId as number,
				signal,
			}),
		enabled: !!purchaseOrderId,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

export const useItemRolls = (purchaseOrderItemId: number | null) =>
	useQuery({
		queryKey: ["rollsReceived", "rolls", purchaseOrderItemId],
		queryFn: ({ signal }) =>
			getItemRolls({
				purchaseOrderItemId: purchaseOrderItemId as number,
				signal,
			}),
		enabled: !!purchaseOrderItemId,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

export const useUpsertItemRoll = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (params: UpsertItemRollParams) => upsertItemRoll(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["rollsReceived", "rolls"] });
			queryClient.invalidateQueries({ queryKey: ["rollsReceived", "items"] });
		},
	});
};

export const useRemoveItemRoll = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number) => removeItemRoll(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["rollsReceived", "rolls"] });
			queryClient.invalidateQueries({ queryKey: ["rollsReceived", "items"] });
		},
	});
};
